import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GraphQLError } from 'graphql'
import { Callback, CartId } from 'lib/types'
import { PWAThunk } from 'lib/core/data'
import { setCart } from '../cart'
import { fetchCartId as fetchGuestCartId } from '../cartId'
import { Slice, AddProductSlice, SimpleProductItem, ConfigurableProductItem } from './types'
import { triggerSimpleAddToCart } from './gtm/triggerSimpleAddToCart'
import { triggerConfigurableAddToCart } from './gtm/triggerConfigurableAddToCart'
import { getCart } from 'lib/core/data/quote/cart/selectors'

export const initialState: Slice = {}

interface ActionData {
  sku: string
  errorCode?: string
}

const getSlice = (slice: AddProductSlice): AddProductSlice => {
  return slice ?? ({
    isFetching: false,
    isSuccess: false,
    isFailure: false,
    errorCode: undefined
  })
}

const slice = createSlice({
  name: 'quote/addProduct',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { sku } = action.payload
      const slice = getSlice(state[sku])
      slice.isFetching = true
      slice.errorCode = undefined
      state[sku] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { sku } = action.payload
      const slice = getSlice(state[sku])
      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
      slice.errorCode = undefined
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { sku, errorCode } = action.payload
      const slice = getSlice(state[sku])
      slice.isFetching = false
      slice.isSuccess = false
      slice.isFailure = true
      slice.errorCode = errorCode
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function addConfigurableProduct (
  _cartId: CartId,
  sku: string,
  cartItems: ConfigurableProductItem[],
  simpleProducts?: SimpleProductItem[],
  callback?: Callback
): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    let configResponse
    let simpleResponse
    let cartId: CartId = _cartId

    dispatch(request({ sku }))

    if (!cartId) {
      cartId = await dispatch(fetchGuestCartId()) as unknown as CartId
      if (!cartId) {
        cartId = ''
      }
    }

    try {
      configResponse = await magentoClient.getClient().quote.addConfigurableProduct({
        cartId,
        cartItems
      })

      let cart = configResponse?.data?.addConfigurableProductsToCart?.cart

      if (simpleProducts?.length) {
        simpleResponse = await magentoClient.getClient().quote.addSimpleProduct({
          cartId,
          cartItems: simpleProducts
        })
        cart = simpleResponse?.data?.addSimpleProductsToCart?.cart
      }

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart
        }))

        triggerConfigurableAddToCart({
          simpleProducts,
          cartItems,
          cart
        })

        dispatch(success({ sku }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ sku, errorCode: gerErrorCode(configResponse?.errors ?? simpleResponse?.errors) }))
        callback?.onFailure?.({
          error: new Error(`[Add Product - Configurable] Unexpected payload: ${JSON.stringify(configResponse)} - ${JSON.stringify(simpleResponse)}`),
          extraData: {
            cartId
          }
        })
      }
    } catch ({ message, graphQLErrors }) {
      dispatch(failure({ sku, errorCode: gerErrorCode(graphQLErrors) }))
      callback?.onFailure?.({
        error: new Error(`[Add Product - Configurable] ${message}`),
        extraData: {
          cartId
        }
      })
    }
  }
}

export function addSimpleProduct (_cartId: CartId, sku: string, cartItems: SimpleProductItem[], callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    let cartId: CartId = _cartId

    dispatch(request({ sku }))

    if (!cartId) {
      cartId = await dispatch(fetchGuestCartId()) as unknown as CartId
      if (!cartId) {
        cartId = ''
      }
    }

    try {
      const response = await magentoClient.getClient().quote.addSimpleProduct({
        cartId,
        cartItems
      })

      const cart = response?.data?.addSimpleProductsToCart?.cart

      const pwaCart = getCart(cartId)(getState())

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart
        }))
        triggerSimpleAddToCart({
          simpleProducts: cartItems,
          cart,
          pwaCart: pwaCart?.data
        })
        dispatch(success({ sku }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ sku, errorCode: gerErrorCode(response?.errors) }))
        callback?.onFailure?.({
          error: new Error(`[Add Product - Simple] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            cartId,
            sku,
            cartItems
          }
        })
      }
    } catch ({ message, graphQLErrors }) {
      dispatch(failure({ sku, errorCode: gerErrorCode(graphQLErrors) }))
      callback?.onFailure?.({
        error: new Error(`[Add Product - Simple] ${message}`),
        extraData: {
          cartId,
          sku,
          cartItems
        }
      })
    }
  }
}

export function addGiftCardProduct (
  _cartId: CartId,
  sku: string,
  cartItems: SimpleProductItem[],
  message: string,
  senderName: string,
  senderEmail: string,
  recipientName: string,
  recipientEmail: string,
  giftCardAmount: string,
  productId: string,
  callback?: Callback
): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    let cartId: CartId = _cartId

    dispatch(request({ sku }))

    if (!cartId) {
      cartId = await dispatch(fetchGuestCartId()) as unknown as CartId
      if (!cartId) {
        cartId = ''
      }
    }

    const payload = {
      cartId,
      cartItems,
      message,
      senderName,
      senderEmail,
      recipientName,
      recipientEmail,
      giftCardAmount,
      productId
    }

    try {
      const response = await magentoClient.getClient().quote.addGiftCardProduct(payload)

      const cart = response?.data?.addGiftcardProductsToCart?.cart

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart
        }))
        // TODO: implement GTM to track giftcardAddToCart
        dispatch(success({ sku }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ sku, errorCode: gerErrorCode(response?.errors) }))
        callback?.onFailure?.({
          error: new Error(`[Add Product - GiftCard] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            sku,
            ...payload
          }
        })
      }

      return response
    } catch ({ message, graphQLErrors }) {
      dispatch(failure({ sku, errorCode: gerErrorCode(graphQLErrors) }))
      callback?.onFailure?.({
        error: new Error(`[Add Product - GiftCard] ${message}`),
        extraData: {
          sku,
          ...payload
        }
      })
    }
  }
}

const gerErrorCode = (errors?: (readonly GraphQLError[])) => {
  const errorMap = [
    {
      message: 'The requested qty is not available',
      code: 'requestedQtyNotAvailable'
    },
    {
      message: 'The requested qty exceeds the maximum qty allowed in shopping cart',
      code: 'requestedQtyNotAllowed'
    }
  ]

  if (!errors?.length) {
    return
  }

  return errorMap.find(({ message }) => errors.some(error => error.message.includes(message)))?.code
}
